import React from 'react';
import { SpatialNavigationMetric } from '../interfaces/types';
import { Map, Navigation, ArrowUpRight, Clock, AlertTriangle } from 'lucide-react';
import { analyzeSpatialNavigation, formatTrend } from '../utils/analysisUtils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface SpatialNavigationTrackerProps {
  metrics: SpatialNavigationMetric[];
}

const SpatialNavigationTracker: React.FC<SpatialNavigationTrackerProps> = ({ metrics }) => {
  // Analyze navigation metrics
  const { averageEfficiency, efficiencyTrend, problematicRoutes, hesitationRate } = analyzeSpatialNavigation(metrics);
  
  // Sort metrics for chart (oldest first for time-series)
  const sortedMetrics = [...metrics]
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    .slice(-14); // Last 14 navigations for chart
  
  // Format timestamp
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Format date for tooltip
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };
  
  // Prepare chart data
  const chartData = sortedMetrics.map(metric => ({
    time: formatTime(metric.timestamp),
    date: formatDate(metric.timestamp),
    pathEfficiency: metric.pathEfficiency,
    wrongTurns: metric.wrongTurns,
    hesitations: metric.hesitations,
    timeToComplete: metric.timeToComplete
  }));
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center mb-4">
        <Map className="text-purple-500 mr-2" />
        <h3 className="text-lg font-semibold">Spatial Navigation Efficiency</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-1">Average Efficiency</h4>
          <div className="flex items-end">
            <span className="text-2xl font-semibold">{averageEfficiency.toFixed(1)}%</span>
            <span className={`ml-2 text-sm ${
              efficiencyTrend > 0 ? 'text-green-500' : 
              efficiencyTrend < 0 ? 'text-red-500' : 
              'text-gray-500'
            }`}>
              {formatTrend(efficiencyTrend)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {averageEfficiency >= 85 
              ? 'Excellent navigation efficiency' 
              : averageEfficiency >= 70
              ? 'Good navigation efficiency'
              : averageEfficiency >= 50
              ? 'Fair navigation efficiency'
              : 'Poor navigation efficiency'}
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-1">Hesitation Rate</h4>
          <div className="flex items-end">
            <span className="text-2xl font-semibold">{hesitationRate.toFixed(1)}</span>
            <span className="ml-1 text-sm text-gray-500">per route</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {hesitationRate <= 1 
              ? 'Minimal hesitation during navigation' 
              : hesitationRate <= 3
              ? 'Moderate hesitation during navigation'
              : 'Frequent hesitations - possible disorientation'}
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-1">Routes Monitored</h4>
          <div className="flex items-end">
            <span className="text-2xl font-semibold">{metrics.length}</span>
            <span className="ml-1 text-sm text-gray-500">total paths</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {metrics.length < 5 
              ? 'Limited data - continue monitoring' 
              : metrics.length < 15
              ? 'Building baseline data'
              : 'Sufficient data for accurate analysis'}
          </p>
        </div>
      </div>
      
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-2">Navigation Efficiency Trend</h4>
        <div className="h-64">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis domain={[0, 100]} />
                <Tooltip 
                  formatter={(value) => [`${value}`, 'Efficiency']}
                  labelFormatter={(label) => {
                    const currentPayload = chartData.find(item => item.time === label);
                    return currentPayload ? `${currentPayload.date} ${label}` : label;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="pathEfficiency" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  name="Path Efficiency"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <p>No navigation data available yet</p>
            </div>
          )}
        </div>
      </div>
      
      {problematicRoutes.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-2">
            Problematic Routes
            {problematicRoutes.length > 0 && (
              <span className="ml-2 text-xs text-red-500">
                {problematicRoutes.length} {problematicRoutes.length === 1 ? 'route' : 'routes'} with efficiency issues
              </span>
            )}
          </h4>
          <div className="space-y-2">
            {problematicRoutes.slice(0, 3).map((route, index) => (
              <div key={index} className="border rounded-lg p-3 bg-red-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <Navigation size={16} className="text-red-500 mr-2" />
                    <div>
                      <div className="flex items-center">
                        <span className="text-sm font-medium">{route.start}</span>
                        <ArrowUpRight size={14} className="mx-1 text-gray-400" />
                        <span className="text-sm font-medium">{route.end}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {route.efficiency < 50 
                          ? 'Significant navigation difficulties' 
                          : 'Minor navigation inefficiencies'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-medium ${
                      route.efficiency < 50 ? 'text-red-500' : 'text-yellow-500'
                    }`}>
                      {route.efficiency.toFixed(1)}%
                    </span>
                    <p className="text-xs text-gray-500">efficiency</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <h4 className="text-sm font-medium mb-2">Recent Navigation Events</h4>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {metrics.slice(0, 5).map((metric, index) => (
          <div key={index} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center mb-1">
                  <span className="text-xs text-gray-500">
                    {formatTime(metric.timestamp)}
                  </span>
                  {metric.wrongTurns > 0 && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {metric.wrongTurns} wrong {metric.wrongTurns === 1 ? 'turn' : 'turns'}
                    </span>
                  )}
                  {metric.hesitations > 2 && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {metric.hesitations} hesitations
                    </span>
                  )}
                </div>
                <div className="flex items-center text-sm">
                  <span className="font-medium">{metric.startLocation}</span>
                  <ArrowUpRight size={14} className="mx-1 text-gray-400" />
                  <span className="font-medium">{metric.endLocation}</span>
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center text-xs text-gray-500 justify-end">
                  <Clock size={14} className="mr-1" />
                  <span>{Math.round(metric.timeToComplete)}s</span>
                </div>
                <div className="mt-1 text-xs">
                  <span className={`font-medium ${
                    metric.pathEfficiency >= 80 ? 'text-green-500' :
                    metric.pathEfficiency >= 60 ? 'text-yellow-500' :
                    'text-red-500'
                  }`}>
                    {metric.pathEfficiency.toFixed(1)}% efficient
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600">
              <div>
                <span className="block text-gray-500">Distance</span>
                <span>{metric.distanceCovered.toFixed(1)}m</span>
              </div>
              <div>
                <span className="block text-gray-500">Familiarity</span>
                <span>{metric.routineFamiliarity.toFixed(1)}%</span>
              </div>
              <div>
                <span className="block text-gray-500">Hesitations</span>
                <span>{metric.hesitations}</span>
              </div>
            </div>
            
            {(metric.wrongTurns > 1 || metric.hesitations > 3) && (
              <div className="mt-2 text-xs flex items-start text-red-600 bg-red-50 p-2 rounded">
                <AlertTriangle size={14} className="mr-1 flex-shrink-0 mt-0.5" />
                <span>
                  {metric.wrongTurns > 1 && metric.hesitations > 3 
                    ? 'Multiple wrong turns and frequent hesitations suggest spatial disorientation'
                    : metric.wrongTurns > 1
                    ? 'Multiple wrong turns may indicate difficulty with spatial recall'
                    : 'Frequent hesitations may indicate uncertainty in navigation'}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpatialNavigationTracker; 